// Labor & rate evidence (C1.1-07): this Company's own attributed LCATs and the
// rate observations associated with them. Driven by react-query hooks
// (companyEvidenceHooks.ts) rather than a direct useEffect + deliveryClient
// call — the shortcut the salvaged version took before this ticket's scope
// covered fixing it.
//
// Evidence follows the company profile id ONLY: a predecessor's, parent's or
// JV partner's rows never appear here, because lineage connects separate
// identities rather than merging their facts (C1.1-04/C1.1-08). Editing
// definitions, mappings and observations uses their canonical workflows
// (Labor Categories / Pricing Sheets), not another rate editor here.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useCompanyLaborEntries, useCompanyRateObservations } from './companyEvidenceHooks';
export function CompanyEvidenceSection({ tenantId, profileId }) {
    const labor = useCompanyLaborEntries(tenantId, profileId);
    const rates = useCompanyRateObservations(tenantId, profileId);
    const isLoading = labor.isLoading || rates.isLoading;
    const isError = labor.isError || rates.isError;
    const laborEntries = labor.data ?? [];
    const rateObservations = rates.data ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        "data-testid": Testids.deliveryRegistries.companyEvidenceSection,
        children: [
            /*#__PURE__*/ _jsx("div", {
                // specra-allow text-px, arbitrary-length: matches the established Registries section-heading pattern (see CompanyLineageSection.tsx)
                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                children: "Labor & rate evidence"
            }),
            isLoading ? /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Loading…"
            }) : isError ? /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Could not load labor/rate evidence."
            }) : laborEntries.length === 0 && rateObservations.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "No labor categories or rates recorded against this company."
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    laborEntries.map((l)=>/*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.deliveryRegistries.companyLaborEvidenceRow,
                            className: "rounded-md border border-border/60 p-2 text-[length:var(--fs-ui-caption)]",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium",
                                    children: l.title
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-muted-foreground",
                                    children: [
                                        l.description,
                                        l.version ? `v${l.version}` : ''
                                    ].filter(Boolean).join(' · ')
                                })
                            ]
                        }, l.id)),
                    rateObservations.map((r)=>/*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.deliveryRegistries.companyRateEvidenceRow,
                            className: "rounded-md border border-border/60 p-2 text-[length:var(--fs-ui-caption)]",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium",
                                    children: r.lcatTitle
                                }),
                                ' ',
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "text-muted-foreground",
                                    children: [
                                        [
                                            r.vehicleName,
                                            r.observationType,
                                            r.rateUnit,
                                            r.periodLabel,
                                            r.source
                                        ].filter(Boolean).join(' · '),
                                        ' ',
                                        "— $",
                                        r.amount.toFixed(2)
                                    ]
                                })
                            ]
                        }, r.id))
                ]
            })
        ]
    });
}
